import { Alert, Box, Divider, Heading, Paragraph, Popover } from '@zillow/constellation-10';

import { DatarouterError } from '@zg-rentals/rental-manager-env';

import { useMultiplexDetails } from '../../../../hooks/queries/multiplex';

import { DEFAULT_ERROR_MESSAGE } from '../../../../constants/ErrorMessageConstants';

import { FinancialExpectationsIntroAndFields } from '../FinancialExpectations/components/FinancialExpectationsIntroAndFields';
import PetPolicyWizardTipContent from '../PetPolicyAndPetFee/components/PetPolicyWizardTipContent';

import { wizardHeadingStyles, wizardParagraphStyles } from '../../../../styles/wizard';

import AmenitiesWithAdditionalAmenitiesFields from '../../../PostingPath/AmenitiesWithAdditionalAmenitiesFields';
import PhotoUploadFormField from '../../../PostingPath/MediaContent/PhotoUploadFormField';
import PetsAllowedFormField from '../../../PostingPath/PetsAllowedFormField';
import PropertyDescriptionFormField from '../../../PostingPath/PropertyDescriptionFormField';
import PageWizard from '../../../Wizard/PageWizard';
import { LeaseTermsIntroAndFields } from '../LeaseTerms';

import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const MultiplexPropertyDetails = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: WizardBodyTemplateProps) => {
  const { error } = useMultiplexDetails();
  const multiplexDetailsErrorMessage = error ? DatarouterError.getDisplayMessage(error, DEFAULT_ERROR_MESSAGE) : null;

  return (
    <PageWizard
      {...rest}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onGoToNextStep || undefined}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      hideBackButton={true}
      displayPremiumTopOfFunnelHeader={false}
    >
      <div data-testid="MultiplexPropertyDetails">
        <Heading level={2} textStyle="heading-sm" css={wizardHeadingStyles}>
          Property details
        </Heading>

        {error ? (
          <Alert tone="critical" css={{ marginBlockEnd: '300' }}>
            {multiplexDetailsErrorMessage}
          </Alert>
        ) : (
          <>
            <Paragraph css={{ marginBottom: '600' }}>
              These details apply to all units by default. You can adjust or add to these for individual units later.
              This step is optional — you can skip it.
            </Paragraph>

            <Box data-testid="FormSectionContainer-FormFields">
              <Box data-testid="SectionContainer-AddPhotos">
                <Heading level={3} textStyle="heading-sm" css={wizardHeadingStyles}>
                  Photos
                </Heading>
                <Paragraph css={wizardParagraphStyles}>Photos help renters imagine living in your place.</Paragraph>
                <PhotoUploadFormField />
              </Box>

              <Divider css={{ marginBlock: '400' }} />

              <Box data-testid="SectionContainer-PropertyDescription">
                <Heading level={3} textStyle="heading-sm" css={wizardHeadingStyles}>
                  Description
                </Heading>
                <PropertyDescriptionFormField />
              </Box>

              <Divider css={{ marginBlock: '400' }} />

              <Box data-testid="SectionContainer-FinancialExpectations">
                <Heading level={3} textStyle="heading-sm" css={wizardHeadingStyles}>
                  Financial expectations
                </Heading>
                <FinancialExpectationsIntroAndFields />
              </Box>

              <Divider css={{ marginBlock: '400' }} />

              <Box data-testid="SectionContainer-PetsAllowed">
                <Box
                  css={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginBottom: '400',
                  }}
                >
                  <Heading level={3} textStyle="heading-sm" css={{ display: 'block' }}>
                    Pets
                  </Heading>
                  <Popover
                    tone="info"
                    placement="top"
                    header={<Heading level={6}>Renters love their pets</Heading>}
                    body={<PetPolicyWizardTipContent />}
                    trigger={<Popover.Trigger css={{ marginLeft: '100' }} />}
                  />
                </Box>
                <PetsAllowedFormField />
              </Box>

              <Divider css={{ marginBlock: '400' }} />

              <Box data-testid="SectionContainer-LeaseTerms">
                <Heading level={3} textStyle="heading-sm" css={wizardHeadingStyles}>
                  Lease terms
                </Heading>
                <LeaseTermsIntroAndFields showMultiplexTips={true} />
              </Box>

              <Divider css={{ marginBlock: '400' }} />

              <Box data-testid="SectionContainer-Amenities">
                <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '400' }}>
                  <Heading level={3} textStyle="heading-sm" css={{ marginBottom: 0 }}>
                    Amenities
                  </Heading>
                  <Popover
                    tone="info"
                    header={<Heading level={6}>Amenities</Heading>}
                    body={
                      <Paragraph>
                        Property amenities shown on all unit listings. Renters often filter by these, so include
                        everything available.
                      </Paragraph>
                    }
                    trigger={<Popover.Trigger css={{ marginLeft: '100' }} />}
                  />
                </Box>
                <Paragraph css={{ marginBottom: '400' }}>
                  Sharing more will help renters see themselves in your home.
                </Paragraph>
                <AmenitiesWithAdditionalAmenitiesFields additionalAmenitiesTopSpacing />
              </Box>
            </Box>
          </>
        )}
      </div>
    </PageWizard>
  );
};

export default MultiplexPropertyDetails;
